import { useState } from 'react';

import { Link } from 'react-router-dom';

import { Button, FieldLabel, showSnackbar, TextInput } from 'fluxo-ui';
import { BackIcon } from 'fluxo-ui/icons';

import { AuthShell, StatusCircle } from '../components/common';
import { ROUTES } from '../common/constants';
import authService from '../services/auth.service';

const ForgotPasswordPage: React.FC = () => {
    const [email, setEmail] = useState('');
    const [errors, setErrors] = useState<{ email?: string }>({});
    const [isLoading, setIsLoading] = useState(false);
    const [isSubmitted, setIsSubmitted] = useState(false);

    const validate = () => {
        const newErrors: { email?: string } = {};

        if (!email.trim()) {
            newErrors.email = 'Email is required';
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
            newErrors.email = 'Invalid email format';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!validate()) {
            return;
        }

        setIsLoading(true);
        try {
            await authService.forgotPassword(email.trim());
            setIsSubmitted(true);
            showSnackbar('Password reset instructions have been sent to your email', 'Email Sent', { type: 'success' });
        } catch (err: any) {
            showSnackbar(err.message || 'Failed to send reset email', 'Error', { type: 'error' });
        } finally {
            setIsLoading(false);
        }
    };

    if (isSubmitted) {
        return (
            <AuthShell>
                <div className="text-center">
                    <StatusCircle tone="success" className="mx-auto mb-5" />
                    <h1 className="text-2xl font-bold tracking-tight text-slate-900 dark:text-white mb-2">Check your email</h1>
                    <p className="text-sm text-slate-500 dark:text-slate-400 mb-6">
                        We've sent password reset instructions to <strong className="text-slate-700 dark:text-slate-200">{email}</strong>
                    </p>
                    <p className="text-sm text-slate-500 dark:text-slate-400 mb-6">
                        Didn't receive the email? Check your spam folder or try again.
                    </p>
                    <div className="space-y-3">
                        <Button
                            label="Try Another Email"
                            variant="default"
                            layout="outlined"
                            className="w-full"
                            onClick={() => {
                                setIsSubmitted(false);
                                setEmail('');
                            }}
                        />
                        <Link to={ROUTES.LOGIN} className="block">
                            <Button label="Back to Login" variant="primary" className="w-full" leftIcon={BackIcon} />
                        </Link>
                    </div>
                </div>
            </AuthShell>
        );
    }

    return (
        <AuthShell>
            <div className="text-center mb-8">
                <h1 className="text-2xl font-bold tracking-tight text-slate-900 dark:text-white mb-2">Forgot password?</h1>
                <p className="text-sm text-slate-500 dark:text-slate-400">
                    Enter your email and we'll send you instructions to reset your password
                </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
                <FieldLabel label="Email" required error={errors.email}>
                    <TextInput
                        value={email}
                        onChange={(e) => {
                            setEmail(e.value);
                            if (errors.email) setErrors({ ...errors, email: undefined });
                        }}
                        placeholder="Enter your email"
                        autoComplete="email"
                        disabled={isLoading}
                        autoFocus
                    />
                </FieldLabel>

                <Button
                    type="submit"
                    label="Send Reset Instructions"
                    variant="primary"
                    className="w-full"
                    isLoading={isLoading}
                    disabled={isLoading}
                />
            </form>

            <div className="mt-6 text-center">
                <Link
                    to={ROUTES.LOGIN}
                    className="inline-flex items-center text-sm text-teal-600 hover:text-teal-700 dark:text-teal-400 dark:hover:text-teal-300 transition-colors"
                >
                    <BackIcon className="w-4 h-4 mr-1" />
                    Back to login
                </Link>
            </div>
        </AuthShell>
    );
};

export default ForgotPasswordPage;
